import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import useScrollLock from '../../hooks/useScrollLock';
import './Salon.css';

const todayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

// Counts weekdays only (Mon-Fri), excluding national holidays - matches the server's calculation.
const calculateDaysCount = (startDate, endDate, holidayDates = new Set()) => {
  if (!startDate || !endDate) return 0;
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  const cursor = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);
  if (cursor > end) return 0;

  let count = 0;
  while (cursor <= end) {
    const dayOfWeek = cursor.getDay();
    const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
    if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dateStr)) {
      count++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const RequestPtoModal = ({ isOpen, onClose, salonId, onSubmitted }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ start_date: todayString(), end_date: todayString(), reason: '' });
  const [holidayDates, setHolidayDates] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setFormData({ start_date: todayString(), end_date: todayString(), reason: '' });
      setError('');
    }
  }, [isOpen]);

  // Fetch national holidays covering the selected range (only needed for the live day-count preview)
  useEffect(() => {
    if (!isOpen || !formData.start_date || !formData.end_date) return;
    const startYear = parseInt(formData.start_date.split('-')[0]);
    const endYear = parseInt(formData.end_date.split('-')[0]);
    const years = startYear === endYear ? [startYear] : [startYear, endYear];

    const fetchHolidays = async () => {
      try {
        const token = localStorage.getItem('token');
        const responses = await Promise.all(years.map((year) =>
          axios.get(`/api/salons/${salonId}/holidays`, { headers: { Authorization: `Bearer ${token}` }, params: { year } })
        ));
        const dates = new Set();
        responses.forEach((r) => (r.data.holidays || []).forEach((h) => dates.add(h.holiday_date)));
        setHolidayDates(dates);
      } catch (err) {
        console.error('Error fetching holidays for pto preview:', err);
      }
    };
    fetchHolidays();
  }, [isOpen, salonId, formData.start_date, formData.end_date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const daysRequested = calculateDaysCount(formData.start_date, formData.end_date, holidayDates);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.start_date || !formData.end_date) {
      setError(t('timeOff.pto.datesRequired'));
      return;
    }
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      setError(t('timeOff.pto.endBeforeStart'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/salons/${salonId}/pto/requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoading(false);
      onSubmitted();
    } catch (err) {
      console.error('Error submitting PTO request:', err);
      setError(err.response?.data?.error || t('timeOff.pto.failedToSubmit'));
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{t('timeOff.pto.requestPto')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit} id="request-pto-form" noValidate>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">{t('timeOff.pto.startDate')} *</label>
                <input type="date" id="start_date" name="start_date" value={formData.start_date} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">{t('timeOff.pto.endDate')} *</label>
                <input type="date" id="end_date" name="end_date" value={formData.end_date} onChange={handleChange} />
              </div>
            </div>

            {daysRequested > 0 && (
              <div className="time-off-summary">
                {t('timeOff.pto.daysRequested', undefined, { count: daysRequested })}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="reason">{t('timeOff.pto.reason')}</label>
              <textarea id="reason" name="reason" value={formData.reason} onChange={handleChange} maxLength={500} rows={3} />
            </div>

            {error && <div className="error-message">{error}</div>}
          </form>
        </div>

        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="request-pto-form" className="salon-button primary" disabled={loading}>
            {loading ? `${t('common.submit')}...` : t('common.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestPtoModal;
